import { MANAGER_ROLES, TEAM_ROLES, WORKSPACE_ROLES } from "./articleRoles";

const CATEGORY = "Getting started";

export const GETTING_STARTED_ARTICLES = [
   {
      id: "getting-around",
      category: CATEGORY,
      title: "Workspaces, projects and surveys",
      summary: "How KAPsurvey is organized and how to find your way around.",
      roles: WORKSPACE_ROLES,
      related: ["create-project", "project-dashboard", "create-survey"],
      body: `
KAPsurvey organizes your work in three levels:

- **Workspaces** group related work, for example one per programme or department.
- **Projects** live inside a workspace and hold the surveys, audiences, assignments and team for one piece of work.
- **Surveys** live inside a project.

## Find your way

1. After you sign in, you see the **Workspace list**. Click a workspace card to enter it.
2. The **Project List** shows the projects in that workspace. Click a project to open it.
3. The project opens on its **Project dashboard**. The sidebar takes you to everything in the project:
   - **Build**: **Audience** and **Surveys**
   - **Distribute**: **Automated Campaigns** and **Assignments**
   - **Analyze**: **Survey Analytics**
   - **Project Settings**: **Team** and **Deleted items**

To move up again, use **Back to Projects** or **Back to Workspaces** at the bottom of the sidebar.

You only see the workspaces and projects you've been added to, and some sidebar items depend on your role. See [Roles and permissions](#roles-and-permissions).
`
   },
   {
      id: "manage-workspaces",
      category: CATEGORY,
      title: "Create and manage workspaces",
      summary: "Add, rename and delete the workspaces your projects live in.",
      roles: TEAM_ROLES,
      related: ["getting-around", "create-project"],
      body: `
The Organization and Senior Managers can create, edit and delete workspaces.

## Create a workspace

1. On the **Workspace list**, click **New workspace**.
2. Enter a **Workspace name** (up to 100 characters) and, optionally, a description.
3. Click **Add workspace**.

The number of workspaces you can create depends on your plan. If you reach the limit, a message says how many workspaces your plan includes.

## Edit or delete a workspace

Open the **⋮** menu on a workspace card and choose **Edit** or **Delete**.

A workspace whose projects have surveys can't be deleted. Deleted workspaces can be restored from **Deleted items**, on the **Workspaces** tab.
`
   },
   {
      id: "create-project",
      category: CATEGORY,
      title: "Create a project",
      summary: "Set up a project with a manager, team, countries and a deadline.",
      roles: MANAGER_ROLES,
      related: ["getting-around", "project-dashboard", "project-team"],
      body: `
1. Open a workspace and click **Create new project** on the **Project List**.
2. Fill in the project details:
   - **Project Title** (required) and **Project Description**.
   - **Project Manager** (required): pick from your organization's members.
   - **Team Members**: optional; you can add people later.
   - **Country** (required): one or more countries where you'll collect data.
   - **Deadline** (required).
3. Click **Create project**. The new project opens on its dashboard.

> **Tip:** The project manager list only shows members who already exist. If it's empty, add people first. See [Manage your organization's members](#organization-members).

## Edit or delete a project

- To change the details, open the **Project dashboard** and click **Edit**.
- To delete a project, click the bin icon on its row in the **Project List**. Deleted projects can be restored from **Deleted projects** for 60 days.
`
   },
   {
      id: "project-dashboard",
      category: CATEGORY,
      title: "Read the project dashboard",
      summary: "Track your project's goal, progress and every survey's status in one place.",
      roles: WORKSPACE_ROLES,
      related: ["survey-analytics", "getting-around"],
      body: `
The **Project dashboard** opens when you enter a project. Its numbers come from the project's published surveys.

- **Project Goal**: the total target of all published surveys.
- **Completion Rate**, **Total Surveys** and **Days Remaining**.
- **Collection progress**: responses collected against the goal, how much of the project's time has passed, and how many surveys per day you need versus how many you're collecting.
- **Survey Status**: one row per survey with its target, responses collected, progress and days left. The status shows **On track**, **Completed**, **At risk** or **Delay**. Click a row's arrow to open that survey's results.

Click **Download** to save the dashboard as a PDF. If your role allows it, click **Edit** to change the project's details.
`
   }
];
